import { Box, useTheme } from '@mui/material'
import { DateTime } from 'luxon'
import { TimeString } from '../../types/models'
import { Series } from '../../utils/series'
import { RelayData } from '../../types/charts'

export interface RelayBarProps {
  relay: RelayData<number>[]
  from: TimeString
  to: TimeString
}

/**
 * Today's on/off history of a relay as a bar, with a marker at "now".
 *
 * The geometry that depends on the current time (the width of the "now" line
 * and the position/size of the bars touching "now") is passed as inline
 * `style`, never through `sx`: Emotion turns every distinct `sx` value into a
 * new class and CSS rule that it caches for the lifetime of the page, and this
 * component is re-rendered on every websocket update (App's `useDevices`
 * refresh). With the percentages in `sx` each update added three rules that
 * were never released, and a home page left open for a couple of days grew to
 * hundreds of thousands of rules — Firefox died with "out of memory"
 * (v0.162.572, 2026-09-25/26).
 */
export function RelayBar(props: RelayBarProps): React.ReactElement {

  const { relay, from, to } = props
  const theme = useTheme()
  const bars = Series.relay(relay, from, to)

  const fromStamp = DateTime.fromISO(from).toMillis()
  const toStamp = DateTime.fromISO(to).toMillis()
  const now = Math.min(new Date().getTime(), toStamp + 1)
  const pos = (now - fromStamp) / (toStamp - fromStamp)

  return (
    <Box>
      <Box sx={{
        position: 'relative',
        mt: 1,
        width: '100%',
        height: '0.5rem'
      }}>
        {/* Bacground dim line through */}
        <Box sx={{
          width: '100%',
          height: 0,
          borderTop: `1px solid ${theme.palette.dimColor.main}`,
          backgroundColor: `${theme.palette.dimColor.main}`,
          borderBottom: `1px solid ${theme.palette.dimColor.main}`,
          borderRadius: '1px',
          position: 'absolute',
          top: '50%'
        }}/>

        {/* Bright line until "now" */}
        <Box style={{ width: `${pos * 100}%` }} sx={{
          height: 0,
          borderTop: `1px solid ${theme.palette.primary.main}`,
          backgroundColor: `${theme.palette.primary.main}`,
          borderBottom: `1px solid ${theme.palette.primary.main}`,
          borderRadius: '1px',
          position: 'absolute',
          top: '50%'
        }}/>

        {bars.map((bar, idx) => <Box key={idx} style={{ left: `${bar.spot}%`, width: `${bar.size}%` }} sx={{
          position: 'absolute',
          height: '0.5rem',
          backgroundColor: bar.dimmed ? theme.palette.primary.dark : theme.palette.primary.main,
          borderTopLeftRadius: bar.startEdge ? '0.25rem' : '0',
          borderBottomLeftRadius: bar.startEdge ? '0.25rem' : '0',
          borderTopRightRadius: bar.stopEdge ? '0.25rem' : '0',
          borderBottomRightRadius: bar.stopEdge ? '0.25rem' : '0',
        }}/>)}
      </Box>

    </Box>
  )
}
